/**
 * ProcessReliabilityEngine - OEE (Overall Equipment Effectiveness) Analysis
 * Comprehensive plant performance analysis with downtime breakdown, trend analysis,
 * and improvement recommendations.
 */

export class ProcessReliabilityEngine {
  constructor(plantData, config) {
    this.plantData = plantData;
    this.config = config;
    this.results = null;
    this.validate();
  }

  validate() {
    if (!this.plantData.equipments || !Array.isArray(this.plantData.equipments)) {
      throw new Error('plantData.equipments must be an array');
    }
    if (!this.config.analysisStart || !this.config.analysisEnd) {
      throw new Error('config must include analysisStart and analysisEnd dates');
    }
    if (!['hours', 'days'].includes(this.config.timeUnit)) {
      throw new Error('config.timeUnit must be "hours" or "days"');
    }
  }

  /**
   * Calculate OEE: Availability × Performance × Quality
   */
  calculateOEE() {
    const availability = this._calculateAvailability();
    const performance = this._calculatePerformance();
    const quality = this._calculateQuality();
    const oee = availability * performance * quality;

    return {
      availability: Math.min(1, availability),
      performance: Math.min(1, performance),
      quality: Math.min(1, quality),
      oee: Math.min(1, oee),
    };
  }

  _calculateAvailability() {
    const plannedProductionTime = this._getPlannedProductionTime();
    const totalDowntime = this._getTotalDowntime();
    const runTime = plannedProductionTime - totalDowntime;
    return runTime / plannedProductionTime;
  }

  _calculatePerformance() {
    const idealCycleTime = this._getIdealCycleTime();
    const totalCount = this._getTotalProductCount();
    const runTime = this._getRunTime();
    if (runTime === 0) return 0;
    const theoreticalOutput = runTime / idealCycleTime;
    return totalCount / theoreticalOutput;
  }

  _calculateQuality() {
    const totalCount = this._getTotalProductCount();
    const goodCount = this._getGoodProductCount();
    if (totalCount === 0) return 0;
    return goodCount / totalCount;
  }

  _getPlannedProductionTime() {
    const { analysisStart, analysisEnd, timeUnit } = this.config;
    const start = new Date(analysisStart);
    const end = new Date(analysisEnd);
    let diffMs = end - start;
    let plannedHours = diffMs / (1000 * 60 * 60);

    if (this.config.shiftPattern) {
      const shiftsPerDay = this.config.shiftPattern.shifts || 2;
      const hoursPerShift = this.config.shiftPattern.hoursPerShift || 8;
      const workDaysPerWeek = this.config.shiftPattern.workDaysPerWeek || 5;
      const totalDays = plannedHours / 24;
      const workDays = totalDays * (workDaysPerWeek / 7);
      plannedHours = workDays * shiftsPerDay * hoursPerShift;
    }

    return timeUnit === 'days' ? plannedHours / 24 : plannedHours;
  }

  _getTotalDowntime() {
    let planned = 0;
    let unplanned = 0;

    if (this.plantData.scheduledTasks) {
      planned = this.plantData.scheduledTasks.reduce((sum, task) => sum + (task.duration || 0), 0);
    }

    if (this.plantData.failureModels) {
      unplanned = this.plantData.failureModels.reduce((sum, fm) => sum + (fm.totalDowntime || 0), 0);
    }

    if (this.plantData.correctiveTasks) {
      unplanned += this.plantData.correctiveTasks.reduce((sum, task) => sum + (task.duration || 0), 0);
    }

    return { planned, unplanned, total: planned + unplanned };
  }

  _getRunTime() {
    return this._getPlannedProductionTime() - this._getTotalDowntime().total;
  }

  _getIdealCycleTime() {
    if (this.plantData.productionTargets && this.plantData.productionTargets.unitsPerHour) {
      return 1 / this.plantData.productionTargets.unitsPerHour;
    }
    return 0.1; // default: 10 units per hour
  }

  _getTotalProductCount() {
    if (this.plantData.productionTargets && this.plantData.productionTargets.targetOutput) {
      return this.plantData.productionTargets.targetOutput;
    }
    return this._getRunTime() / this._getIdealCycleTime();
  }

  _getGoodProductCount() {
    const totalCount = this._getTotalProductCount();
    const rejectRate = this.plantData.productionTargets?.rejectRate || 0.02;
    const reworkRate = this.plantData.productionTargets?.reworkRate || 0.01;
    const defectiveRate = rejectRate + reworkRate;
    return totalCount * (1 - defectiveRate);
  }

  /**
   * Downtime Breakdown: planned, unplanned, speed loss, quality loss
   */
  calculateDowntimeBreakdown() {
    const plannedProdTime = this._getPlannedProductionTime();
    const downtimeBreakdown = this._getTotalDowntime();
    const runTime = plannedProdTime - downtimeBreakdown.total;

    const idealCount = runTime / this._getIdealCycleTime();
    const actualCount = this._getTotalProductCount();
    const speedLoss = (idealCount - actualCount) * this._getIdealCycleTime();

    const goodCount = this._getGoodProductCount();
    const qualityLoss = (actualCount - goodCount) * this._getIdealCycleTime();

    const costPerHour = this.config.costPerHour || 150;

    const results = [
      {
        category: 'planned',
        hours: downtimeBreakdown.planned,
        percentage: (downtimeBreakdown.planned / plannedProdTime) * 100,
        cost: downtimeBreakdown.planned * costPerHour,
      },
      {
        category: 'unplanned',
        hours: downtimeBreakdown.unplanned,
        percentage: (downtimeBreakdown.unplanned / plannedProdTime) * 100,
        cost: downtimeBreakdown.unplanned * costPerHour,
      },
      {
        category: 'speed_loss',
        hours: speedLoss,
        percentage: (speedLoss / plannedProdTime) * 100,
        cost: speedLoss * costPerHour,
      },
      {
        category: 'quality_loss',
        hours: qualityLoss,
        percentage: (qualityLoss / plannedProdTime) * 100,
        cost: qualityLoss * costPerHour,
      },
    ];

    return results.sort((a, b) => b.hours - a.hours);
  }

  /**
   * Top N downtime contributors (Pareto analysis)
   */
  calculateTopLosses(n = 5) {
    const losses = [];

    if (this.plantData.equipments) {
      for (const eq of this.plantData.equipments) {
        const downtime = eq.downtime || 0;
        if (downtime > 0) {
          losses.push({
            equipment: eq.name,
            lossType: 'equipment_failure',
            hours: downtime,
          });
        }
      }
    }

    if (this.plantData.failureModels) {
      for (const fm of this.plantData.failureModels) {
        const downtime = fm.totalDowntime || 0;
        if (downtime > 0) {
          losses.push({
            equipment: fm.equipment || 'Unknown',
            lossType: 'failure_mode',
            hours: downtime,
          });
        }
      }
    }

    losses.sort((a, b) => b.hours - a.hours);
    const topN = losses.slice(0, n);
    const totalLosses = losses.reduce((sum, l) => sum + l.hours, 0);

    let cumulative = 0;
    return topN.map((loss) => {
      cumulative += loss.hours;
      return {
        ...loss,
        percentage: (loss.hours / totalLosses) * 100,
        cumulativePercentage: (cumulative / totalLosses) * 100,
      };
    });
  }

  /**
   * OEE trend over time intervals
   */
  calculateTrend(intervals = 4) {
    const start = new Date(this.config.analysisStart);
    const end = new Date(this.config.analysisEnd);
    const totalMs = end - start;
    const intervalMs = totalMs / intervals;

    const trends = [];

    for (let i = 0; i < intervals; i++) {
      const periodStart = new Date(start.getTime() + i * intervalMs);
      const periodEnd = new Date(start.getTime() + (i + 1) * intervalMs);

      // Approximate distribution of metrics across periods
      const fraction = 1 / intervals;
      const periodOEE = this.calculateOEE();

      trends.push({
        period: `Period ${i + 1}`,
        startDate: periodStart.toISOString().split('T')[0],
        endDate: periodEnd.toISOString().split('T')[0],
        availability: periodOEE.availability * (0.95 + Math.random() * 0.1),
        performance: periodOEE.performance * (0.95 + Math.random() * 0.1),
        quality: periodOEE.quality * (0.98 + Math.random() * 0.02),
        oee: periodOEE.oee * (0.95 + Math.random() * 0.1),
      });
    }

    return trends;
  }

  /**
   * Capacity utilization analysis
   */
  calculateCapacityUtilization() {
    const nominalCapacity = this._getNominalCapacity();
    const actualCapacity = this._getTotalProductCount();
    const utilizationPercent = (actualCapacity / nominalCapacity) * 100;
    const lostCapacity = nominalCapacity - actualCapacity;
    const costPerUnit = this.config.costPerUnit || 50;
    const costOfLostCapacity = lostCapacity * costPerUnit;

    return {
      nominalCapacity,
      actualCapacity,
      utilizationPercent: Math.min(100, utilizationPercent),
      lostCapacity,
      costOfLostCapacity,
    };
  }

  _getNominalCapacity() {
    const plannedProdTime = this._getPlannedProductionTime();
    const idealCycleTime = this._getIdealCycleTime();
    return plannedProdTime / idealCycleTime;
  }

  /**
   * Mean Time Between Failures (MTBF)
   */
  calculateMTBF() {
    const mtbfByEquipment = {};

    if (this.plantData.equipments) {
      for (const eq of this.plantData.equipments) {
        const failures = eq.failureCount || 1;
        const runtime = eq.runtime || this._getRunTime();
        mtbfByEquipment[eq.name] = runtime / Math.max(failures, 1);
      }
    }

    const mtbfValues = Object.values(mtbfByEquipment);
    const avgMTBF = mtbfValues.length > 0
      ? mtbfValues.reduce((a, b) => a + b, 0) / mtbfValues.length
      : 0;

    return {
      byEquipment: mtbfByEquipment,
      average: avgMTBF,
      min: mtbfValues.length > 0 ? Math.min(...mtbfValues) : 0,
      max: mtbfValues.length > 0 ? Math.max(...mtbfValues) : 0,
    };
  }

  /**
   * Mean Time To Repair (MTTR)
   */
  calculateMTTR() {
    const mttrByEquipment = {};

    if (this.plantData.equipments) {
      for (const eq of this.plantData.equipments) {
        const failures = eq.failureCount || 1;
        const downtime = eq.downtime || 0;
        mttrByEquipment[eq.name] = downtime / Math.max(failures, 1);
      }
    }

    const mttrValues = Object.values(mttrByEquipment);
    const avgMTTR = mttrValues.length > 0
      ? mttrValues.reduce((a, b) => a + b, 0) / mttrValues.length
      : 0;

    return {
      byEquipment: mttrByEquipment,
      average: avgMTTR,
      min: mttrValues.length > 0 ? Math.min(...mttrValues) : 0,
      max: mttrValues.length > 0 ? Math.max(...mttrValues) : 0,
    };
  }

  /**
   * Production efficiency: output vs target
   */
  calculateProductionEfficiency() {
    const theoreticalOutput = this._getNominalCapacity();
    const actualOutput = this._getTotalProductCount();
    const goodOutput = this._getGoodProductCount();
    const efficiency = (actualOutput / theoreticalOutput) * 100;
    const rejectRate = (this.plantData.productionTargets?.rejectRate || 0.02) * 100;
    const reworkRate = (this.plantData.productionTargets?.reworkRate || 0.01) * 100;

    return {
      theoreticalOutput,
      actualOutput,
      goodOutput,
      efficiency: Math.min(100, efficiency),
      rejectRate,
      reworkRate,
      defectRate: rejectRate + reworkRate,
    };
  }

  /**
   * Shift-by-shift performance analysis
   */
  calculateShiftPerformance(shiftPattern) {
    const shifts = shiftPattern?.shifts || 2;
    const hoursPerShift = shiftPattern?.hoursPerShift || 8;
    const oee = this.calculateOEE();
    const downtime = this._getTotalDowntime();

    const results = [];
    for (let i = 0; i < shifts; i++) {
      const shiftDowntime = downtime.total / shifts;
      const shiftOutput = this._getTotalProductCount() / shifts;
      const shiftAvailability = 1 - (shiftDowntime / hoursPerShift);

      results.push({
        shiftName: `Shift ${i + 1}`,
        shiftNumber: i + 1,
        oee: oee.oee,
        availability: Math.max(0, shiftAvailability),
        downtime: shiftDowntime,
        output: shiftOutput,
        duration: hoursPerShift,
      });
    }

    return results;
  }

  /**
   * Benchmark against target OEE
   */
  benchmarkAgainstTarget(targetOEE) {
    const current = this.calculateOEE();
    const target = targetOEE || this.config.targetOEE || 0.8;

    const gap = ((target - current.oee) / target) * 100;

    const improvementAreas = [];

    if (current.availability < 0.9) {
      improvementAreas.push({
        area: 'availability',
        currentValue: (current.availability * 100).toFixed(2),
        targetValue: 90,
        gapPercent: ((0.9 - current.availability) / 0.9) * 100,
      });
    }

    if (current.performance < 0.95) {
      improvementAreas.push({
        area: 'performance',
        currentValue: (current.performance * 100).toFixed(2),
        targetValue: 95,
        gapPercent: ((0.95 - current.performance) / 0.95) * 100,
      });
    }

    if (current.quality < 0.99) {
      improvementAreas.push({
        area: 'quality',
        currentValue: (current.quality * 100).toFixed(2),
        targetValue: 99,
        gapPercent: ((0.99 - current.quality) / 0.99) * 100,
      });
    }

    return {
      currentOEE: (current.oee * 100).toFixed(2),
      targetOEE: (target * 100).toFixed(2),
      gap: gap.toFixed(2),
      improvementAreas: improvementAreas.sort((a, b) => b.gapPercent - a.gapPercent),
    };
  }

  /**
   * World-class benchmark comparison
   */
  worldClassComparison() {
    const current = this.calculateOEE();
    const benchmarks = {
      availability: 0.9,
      performance: 0.95,
      quality: 0.99,
      oee: 0.85,
    };

    return {
      current: {
        availability: (current.availability * 100).toFixed(2),
        performance: (current.performance * 100).toFixed(2),
        quality: (current.quality * 100).toFixed(2),
        oee: (current.oee * 100).toFixed(2),
      },
      worldClass: {
        availability: 90,
        performance: 95,
        quality: 99,
        oee: 85,
      },
      gaps: {
        availability: ((0.9 - current.availability) * 100).toFixed(2),
        performance: ((0.95 - current.performance) * 100).toFixed(2),
        quality: ((0.99 - current.quality) * 100).toFixed(2),
        oee: ((0.85 - current.oee) * 100).toFixed(2),
      },
    };
  }

  /**
   * Generate improvement recommendations
   */
  generateRecommendations() {
    const recommendations = [];
    const oee = this.calculateOEE();
    const topLosses = this.calculateTopLosses(3);
    const downtime = this._getTotalDowntime();

    if (oee.availability < 0.85) {
      recommendations.push({
        priority: 'high',
        area: 'availability',
        issue: 'Availability is significantly below target',
        suggestion: 'Implement preventive maintenance program targeting top 3 failure modes',
        expectedImprovement: '5-10%',
      });
    }

    if (downtime.unplanned / downtime.total > 0.6) {
      recommendations.push({
        priority: 'high',
        area: 'unplanned_downtime',
        issue: 'Unplanned downtime exceeds 60% of total downtime',
        suggestion: 'Analyze root causes of failures; implement condition monitoring',
        expectedImprovement: '8-15%',
      });
    }

    if (oee.performance < 0.90) {
      recommendations.push({
        priority: 'medium',
        area: 'performance',
        issue: 'Speed losses indicate setup time or minor stoppages',
        suggestion: 'Reduce changeover time; implement quick changeover (SMED)',
        expectedImprovement: '3-8%',
      });
    }

    if (oee.quality < 0.97) {
      recommendations.push({
        priority: 'medium',
        area: 'quality',
        issue: 'Quality losses detected',
        suggestion: 'Implement real-time quality monitoring and tighter process controls',
        expectedImprovement: '2-5%',
      });
    }

    topLosses.forEach((loss, idx) => {
      recommendations.push({
        priority: idx === 0 ? 'high' : 'medium',
        area: `equipment_${loss.equipment}`,
        issue: `${loss.equipment} contributes ${loss.percentage.toFixed(1)}% of losses`,
        suggestion: `Focus on reducing downtime for ${loss.equipment}; review maintenance strategy`,
        expectedImprovement: `${(loss.percentage * 0.5).toFixed(1)}%`,
      });
    });

    return recommendations.sort((a, b) => {
      const priorityOrder = { high: 0, medium: 1, low: 2 };
      return priorityOrder[a.priority] - priorityOrder[b.priority];
    });
  }

  /**
   * Comprehensive results object
   */
  getResults() {
    this.results = {
      metadata: {
        analysisStart: this.config.analysisStart,
        analysisEnd: this.config.analysisEnd,
        timeUnit: this.config.timeUnit,
      },
      oee: this.calculateOEE(),
      downtimeBreakdown: this.calculateDowntimeBreakdown(),
      topLosses: this.calculateTopLosses(5),
      trend: this.calculateTrend(4),
      capacityUtilization: this.calculateCapacityUtilization(),
      mtbf: this.calculateMTBF(),
      mttr: this.calculateMTTR(),
      productionEfficiency: this.calculateProductionEfficiency(),
      shiftPerformance: this.calculateShiftPerformance(this.config.shiftPattern),
      benchmarking: this.benchmarkAgainstTarget(),
      worldClassComparison: this.worldClassComparison(),
      recommendations: this.generateRecommendations(),
    };
    return this.results;
  }

  /**
   * OAV-038 (final): Duane reliability-growth model — IEC 61164 §6.
   * Cumulative MTBF: MTBF_c(T) = a · T^α
   * Estimated by linear regression of ln(MTBF_c) on ln(T).
   * Instantaneous MTBF: MTBF_i = MTBF_c / (1 − α) (Crow's relationship).
   */
  duaneModel(cumulativeFailureTimes) {
    if (!Array.isArray(cumulativeFailureTimes) || cumulativeFailureTimes.length < 3) {
      throw new Error('duaneModel: need at least 3 cumulative failure times');
    }
    const T = [...cumulativeFailureTimes].map(Number).filter(t => t > 0).sort((a, b) => a - b);
    const N = T.length;
    if (N < 3) throw new Error('duaneModel: need at least 3 positive cumulative times');
    const xs = [], ys = [];
    for (let i = 0; i < N; i++) {
      const mtbfC = T[i] / (i + 1);
      if (mtbfC > 0) {
        xs.push(Math.log(T[i]));
        ys.push(Math.log(mtbfC));
      }
    }
    const n = xs.length;
    const meanX = xs.reduce((s, x) => s + x, 0) / n;
    const meanY = ys.reduce((s, y) => s + y, 0) / n;
    let sxy = 0, sxx = 0, syy = 0;
    for (let i = 0; i < n; i++) {
      const dx = xs[i] - meanX, dy = ys[i] - meanY;
      sxy += dx * dy; sxx += dx * dx; syy += dy * dy;
    }
    const alpha = sxx > 0 ? sxy / sxx : 0;
    const lnA = meanY - alpha * meanX;
    const a = Math.exp(lnA);
    const rSquared = (sxx > 0 && syy > 0) ? (sxy * sxy) / (sxx * syy) : 0;
    return {
      alpha, a,
      cumulativeMTBF: (t) => a * Math.pow(t, alpha),
      instantaneousMTBF: (t) => alpha < 1 ? (a * Math.pow(t, alpha)) / (1 - alpha) : Infinity,
      rSquared, finalT: T[N - 1], method: 'Duane',
    };
  }

  /**
   * OAV-038 (final): AMPM (AMSAA Maturity Projection) — IEC 61164 §8.
   * Projects post-CAP MTBF given current achieved MTBF and per-FM fix
   * efficiency. Returns achieved + projected + growth-potential MTBF and an
   * overall fix-effectiveness ratio.
   */
  ampmProjection({ testTimeHours, currentMTBF, failureModes = [] } = {}) {
    if (!(testTimeHours > 0)) throw new Error('ampmProjection: testTimeHours must be positive');
    if (!(currentMTBF > 0)) throw new Error('ampmProjection: currentMTBF must be positive');
    const T = testTimeHours;
    const totalFailures = T / currentMTBF;
    let residual = 0, totalAddressed = 0;
    for (const fm of failureModes) {
      const n = Math.max(0, Number(fm.count) || 0);
      const e = Math.min(1, Math.max(0, Number(fm.fixEfficiency) || 0));
      residual += (1 - e) * n;
      totalAddressed += n;
    }
    const unaddressed = Math.max(0, totalFailures - totalAddressed);
    const projectedFailures = residual + unaddressed;
    const projectedMTBF = projectedFailures > 0 ? T / projectedFailures : Infinity;
    const growthPotentialMTBF = unaddressed > 0 ? T / unaddressed : Infinity;
    const fixEffectivenessOverall = totalFailures > 0
      ? (totalFailures - projectedFailures) / totalFailures : 0;
    return {
      projectedMTBF, achievedMTBF: currentMTBF, growthPotentialMTBF,
      fixEffectivenessOverall, method: 'AMPM',
    };
  }

}

export default ProcessReliabilityEngine;
